/**
 * useAssessmentLock — exclusive edit lock for draft assessments (FECOA-1302).
 *
 * On mount the hook acquires the lock for the current user, then refreshes it
 * on a heartbeat interval so it never goes stale while the tab is open. On
 * unmount (or tab close) it releases the lock.
 *
 * If another user already holds an active lock, acquisition returns HTTP 423.
 * The hook then enters read-only mode, exposes who is editing and since when,
 * and polls the lock status until the holder releases it (or it goes stale),
 * at which point it transparently acquires the lock and editing is re-enabled.
 *
 * Returns:
 *   - isReadOnly: true while another user holds the lock (editing disabled)
 *   - isOwner:    true once this user holds the lock
 *   - holder:     { name, email } of the other editor when read-only
 *   - since:      ISO timestamp the other editor acquired the lock
 *   - enabled:    whether locking applies (draft only)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { acquireLock, releaseLock, getLockStatus } from '../../../../services/assessmentBuilderApi';

const DEFAULT_HEARTBEAT_MS = 45000;
const POLL_INTERVAL_MS = 15000;

export default function useAssessmentLock(assessmentUuid, { enabled = true } = {}) {
  const [isOwner, setIsOwner] = useState(false);
  const [holder, setHolder] = useState(null);
  const [since, setSince] = useState(null);

  const heartbeatTimer = useRef(null);
  const pollTimer = useRef(null);
  const mountedRef = useRef(true);
  const heartbeatMsRef = useRef(DEFAULT_HEARTBEAT_MS);

  const clearTimers = useCallback(() => {
    if (heartbeatTimer.current) { clearInterval(heartbeatTimer.current); heartbeatTimer.current = null; }
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
  }, []);

  const applyOwned = useCallback((data) => {
    if (!mountedRef.current) return;
    setIsOwner(true);
    setHolder(null);
    setSince(null);
    if (data?.heartbeat_interval_seconds) {
      heartbeatMsRef.current = data.heartbeat_interval_seconds * 1000;
    }
  }, []);

  const applyLockedByOther = useCallback((lock) => {
    if (!mountedRef.current) return;
    setIsOwner(false);
    setHolder(lock?.locked_by || null);
    setSince(lock?.acquired_at || null);
  }, []);

  // Try to take (or refresh) the lock. Resolves to true when we own it.
  const tryAcquire = useCallback(async () => {
    try {
      const data = await acquireLock(assessmentUuid);
      // Non-draft assessments are not lockable; treat as owned (read-only is
      // governed by the review workflow, not this hook).
      applyOwned(data);
      return true;
    } catch (err) {
      if (err?.response?.status === 423) {
        applyLockedByOther(err.response.data || {});
        return false;
      }
      // Network/other errors: fail open so a transient blip doesn't strand the
      // editor read-only. The next heartbeat/poll will reconcile.
      return false;
    }
  }, [assessmentUuid, applyOwned, applyLockedByOther]);

  // Poll while locked by someone else; re-acquire as soon as it frees up.
  const startPolling = useCallback(() => {
    if (pollTimer.current) return;
    pollTimer.current = setInterval(async () => {
      try {
        const status = await getLockStatus(assessmentUuid);
        if (!status?.locked) {
          // Free or stale — grab it and stop polling.
          const won = await tryAcquire();
          if (won) {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
          }
        } else {
          applyLockedByOther(status);
        }
      } catch {
        /* transient — keep polling */
      }
    }, POLL_INTERVAL_MS);
  }, [assessmentUuid, tryAcquire, applyLockedByOther]);

  const startHeartbeat = useCallback(() => {
    if (heartbeatTimer.current) return;
    heartbeatTimer.current = setInterval(async () => {
      const owned = await tryAcquire();
      if (!owned) {
        // Lost the lock (e.g. taken over after our tab slept). Switch to polling.
        if (heartbeatTimer.current) { clearInterval(heartbeatTimer.current); heartbeatTimer.current = null; }
        startPolling();
      }
    }, heartbeatMsRef.current);
  }, [tryAcquire, startPolling]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled || !assessmentUuid) return undefined;

    let cancelled = false;
    (async () => {
      const owned = await tryAcquire();
      if (cancelled || !mountedRef.current) return;
      if (owned) {
        startHeartbeat();
      } else {
        startPolling();
      }
    })();

    // Best-effort release if the tab is closed/refreshed.
    const handleUnload = () => {
      try {
        const url = `/api/assessment-builder/assessments/${assessmentUuid}/release-lock/`;
        const token = localStorage.getItem('authToken');
        if (navigator.sendBeacon && token) {
          // sendBeacon cannot set Authorization headers; fall back to a keepalive
          // fetch so the release carries the bearer token.
          fetch(url, { method: 'POST', keepalive: true, headers: { Authorization: `Bearer ${token}` } });
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      cancelled = true;
      mountedRef.current = false;
      clearTimers();
      window.removeEventListener('beforeunload', handleUnload);
      // Release on normal unmount (navigating away within the SPA).
      releaseLock(assessmentUuid).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentUuid, enabled]);

  return {
    isReadOnly: enabled && !!holder && !isOwner,
    isOwner,
    holder,
    since,
    enabled,
  };
}
